// Urgency banding for a plan expiry date, so a bare date carries its meaning.
//
// Bands (days from today, calendar days not elapsed hours — a plan expiring
// "tomorrow" should read as 1 day whether it's 09:00 or 23:00 now):
//   expired   < 0      already gone
//   critical  0 – 2    today, tomorrow, or the day after
//   soon      3 – 7    this week
//   ok        > 7      no signal needed

/** Upper bound (inclusive) of the critical band, in days. */
const CRITICAL_DAYS = 2;
/** Upper bound (inclusive) of the soon band, in days. */
const SOON_DAYS = 7;

/**
 * Whole calendar days from today to `date`. Negative when already past.
 * Both sides are floored to local midnight so the result doesn't flip based on
 * the time of day.
 *
 * @param {string|Date} date
 * @param {Date} [now] - Injectable for tests.
 * @returns {number|null} Null when absent or unparseable.
 */
function daysUntil(date, now = new Date()) {
  if (!date) return null;
  const target = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(target.getTime())) return null;

  const startOfTarget = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate()
  );
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((startOfTarget - startOfToday) / MS_PER_DAY);
}

/**
 * Urgency band for an expiry date.
 *
 * `tone` is semantic, not a colour — callers map it to their own palette.
 * Returns null for a missing date so the UI renders nothing rather than
 * implying a state it doesn't know.
 *
 * @param {string|Date} date
 * @param {Date} [now]
 * @returns {{tone: 'expired'|'critical'|'soon'|'ok', days: number,
 *   label: string}|null}
 */
export function expiryStatus(date, now = new Date()) {
  const days = daysUntil(date, now);
  if (days === null) return null;

  if (days < 0) {
    const ago = Math.abs(days);
    return {
      tone: "expired",
      days,
      label: ago === 1 ? "Expired yesterday" : `Expired ${ago}d ago`,
    };
  }
  if (days <= CRITICAL_DAYS) {
    return {
      tone: "critical",
      days,
      label:
        days === 0 ? "Expires today" : days === 1 ? "Expires tomorrow" : `${days}d left`,
    };
  }
  if (days <= SOON_DAYS) {
    return { tone: "soon", days, label: `${days}d left` };
  }
  return { tone: "ok", days, label: `${days}d left` };
}
